import React, { useEffect, useState, useRef, useCallback } from "react";
import useAxios from "../../utils/useAxios";
import { useInView } from "react-intersection-observer";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { StatisticLazyLoad } from "./StatisticLazyLoad";
import {
    formatLastName,
    getOrdinalSuffix,
    Truncated,
    getInitials,
    getSmallRoundTitle,
    getGroupRoundNumber
} from "../../constants";

// Same status/date-pill rules as TournamentsList.js's own tournament card
// (that's the "1 в 1" reference for this header) - duplicated here rather
// than shared since this endpoint's tournament objects carry a different,
// smaller field set (no date_range/players/thumbnail) than the tournaments
// list's own.
const getTournamentStatus = (tournament) => {
    if (!tournament.start_date || !tournament.end_date) return "upcoming";
    const now = new Date();
    const start = new Date(tournament.start_date);
    const end = new Date(tournament.end_date);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "upcoming";
    if (now < start) return "upcoming";
    if (now > end) return "completed";
    return "live";
};

const isSameCalendarDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const getStatusClass = (tournament) => {
    const status = getTournamentStatus(tournament);
    if (status === "live") return "in-progress";
    if (status === "completed") return "completed";
    const start = tournament.start_date && new Date(tournament.start_date);
    if (start && !isNaN(start.getTime()) && isSameCalendarDay(start, new Date())) return "today";
    return "expected";
};

const formatTournamentTitle = (text) => {
    if (!text) return "";
    return text.toLowerCase().split(" ").map(word => word.split("-").map(part => part.charAt(0).toUpperCase() + part.slice(1)).join("-")).join(" ");
};

// Backend tournaments-list endpoints send a precomputed date_range string
// ("26-27.09", "03.09-27.10", ...) - this endpoint doesn't, so this mirrors
// that same no-year, dash-joined format from start_date/end_date directly.
// Reads the Y/M/D digits straight off the ISO string instead of going
// through `new Date(...).getDate()` - a same-day 18:00-21:00Z tournament in
// a UTC+3 city puts 21:00Z at 00:00 local the next day, so the local-time
// read quietly turned a real single-day tournament into a fake 2-day
// "04-05.09" range.
const dateOnlyParts = (iso) => {
    const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? { y: m[1], m: m[2], d: m[3] } : null;
};
const formatDateRange = (startStr, endStr) => {
    const s = dateOnlyParts(startStr);
    if (!s) return "";
    const e = dateOnlyParts(endStr) || s;
    if (s.y === e.y && s.m === e.m && s.d === e.d) return `${s.d}.${s.m}`;
    if (s.y === e.y && s.m === e.m) return `${s.d}-${e.d}.${s.m}`;
    return `${s.d}.${s.m}-${e.d}.${e.m}`;
};

const deduplicateMatches = (matches) => {
    if (!Array.isArray(matches)) return [];
    const seen = new Set();
    return matches.filter(m => {
        if (!m) return false;
        const key = m.id != null
            ? String(m.id)
            : `${m.player_1}-${m.player_2}-${m.round_number}-${m.group_number}-${m.match_order}-${JSON.stringify(m.sets || [])}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const mergeTournaments = (prevList, incomingList) => {
    const list = [...prevList];
    incomingList.forEach(item => {
        const itemMatches = deduplicateMatches(item.matches || []);
        const existingIdx = list.findIndex(t => t.id != null && item.id != null && String(t.id) === String(item.id));
        if (existingIdx !== -1) {
            const combinedMatches = deduplicateMatches([
                ...(list[existingIdx].matches || []),
                ...itemMatches
            ]);
            list[existingIdx] = {
                ...list[existingIdx],
                ...item,
                matches: combinedMatches
            };
        } else {
            list.push({
                ...item,
                matches: itemMatches
            });
        }
    });
    return list;
};

const renderTeamCompact = (p1, p2, grade, hasWinner, isWinner, id1, id2, onPlayerClick, currentPlayerId) => {
    const g = Number(grade);
    // no winner yet: plain default text. Winner: green (score-win). Loser: gray
    // (score-lose) - was lumping the winner in with the "no winner" black-name
    // case, so a decided match's winner never actually read as the winner.
    const nameClass = !hasWinner ? "black-name text-nowrap" : isWinner ? "score-win text-nowrap" : "score-lose text-nowrap";

    const nameSpan = (name, id, className) => {
        const clickable = !!(id && onPlayerClick && String(id) !== String(currentPlayerId));
        return (
            <span
                className={`${className}${clickable ? " cursor player-link" : ""}`}
                onClick={clickable ? (e) => { e.stopPropagation(); onPlayerClick(id); } : undefined}
            >
                {name}
            </span>
        );
    };

    if (g === 1) {
        if (!p1) return "-";
        let last = "", first = "";
        if (typeof p1 === "string") {
            const parts = p1.split(" ");
            last = parts.shift() || "";
            first = parts.join(" ") || "";
        } else {
            last = p1.last_name || "";
            first = p1.first_name || "";
        }
        return nameSpan(<>{first && `${first[0]}. `}<Truncated text={formatLastName(last)} max={10} /></>, id1, nameClass);
    } else {
        const pArr = [[p1, id1], [p2, id2]].filter(([player]) => Boolean(player));
        if (pArr.length === 0) return "-";
        return (
            <div style={{ padding: "3px 0" }}>
                {pArr.map(([player, id], i) => {
                    let last = "", first = "";
                    if (typeof player === "string") {
                        const parts = player.split(" ");
                        last = parts.shift() || "";
                        first = parts.join(" ") || "";
                    } else {
                        last = player.last_name || "";
                        first = player.first_name || "";
                    }
                    return (
                        <React.Fragment key={i}>
                            {nameSpan(<>{first && `${first[0]}. `}<Truncated text={formatLastName(last)} max={12} /></>, id, nameClass)}
                            {i < pArr.length - 1 && <br />}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }
};

const renderScoreCompact = (match, isSide2) => {
    let winnerSide = Number(match.winner_side);
    if (!winnerSide && match.sets && match.sets.length > 0) {
        let s1 = 0, s2 = 0;
        match.sets.forEach(s => {
            if (s.game_score_1 > s.game_score_2) s1++;
            else if (s.game_score_2 > s.game_score_1) s2++;
        });
        if (s1 > s2) winnerSide = 1;
        else if (s2 > s1) winnerSide = 2;
    }

    const hasWinner = winnerSide === 1 || winnerSide === 2;
    const isWon = isSide2 ? winnerSide === 2 : winnerSide === 1;
    const scoreClass = hasWinner ? (isWon ? "score-win" : "score-lose") : "";

    if (!match.sets || match.sets.length === 0) {
        if (hasWinner) {
            return <span className={scoreClass}>{isWon ? "+/-" : "-/+"}</span>;
        }
        return "-";
    }

    return (
        <span className={scoreClass}>
            {match.sets.map((set, i) => {
                let score1 = isSide2 ? set.game_score_2 : set.game_score_1;
                let score2 = isSide2 ? set.game_score_1 : set.game_score_2;
                
                if (score1 === null || score2 === null) return null;
                return (
                    <span key={i} className="text-nowrap" style={{ display: 'block', lineHeight: '1.2' }}>
                        {score1}-{score2}
                    </span>
                );
            })}
        </span>
    );
};

const PlayerStatistic = ({ playerId, year, grade, club, sport, onPlayerClick }) => {
    const api = useAxios();
    const history = useHistory();
    const [tournaments, setTournaments] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const { ref, inView } = useInView({ threshold: 0.1 });
    const reqRef = useRef(0);
    // /players/{id}/statistics/ carries no club_logo (see the top-of-file
    // comment on the smaller field set) - only the tournament's own detail
    // endpoint has it, so this fetches that per tournament to fill it in,
    // once per id ever (persists across pages, not just this call) rather
    // than re-fetching a tournament this player has multiple matches
    // recorded against. Fire-and-forget, not part of the initial render -
    // cards show initials immediately and swap to the real logo once it
    // lands, same as any other lazy image.
    const logoCache = useRef({});
    const enrichClubLogos = useCallback((list) => {
        const toFetch = list.filter(t => t.id && !(t.id in logoCache.current));
        if (toFetch.length === 0) return;
        Promise.all(toFetch.map(t =>
            api.get(`/api/v1.0/tournaments/${t.id}/`)
                .then(res => { logoCache.current[t.id] = res.data?.club_logo || null; })
                .catch(() => { logoCache.current[t.id] = null; })
        )).then(() => {
            setTournaments(prev => prev.map(t => (
                t.id in logoCache.current ? { ...t, club_logo: logoCache.current[t.id] } : t
            )));
        });
        // api excluded - see loadData's own identical comment below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async (fetchPage) => {
        const thisReq = ++reqRef.current;
        setLoading(true);
        try {
            const limit = 10;
            const offset = (fetchPage - 1) * limit;

            const params = { limit, offset };
            if (year) params.year = year;
            if (grade) params.grade = grade;
            if (club) params.club = club;
            if (sport) params.sport = sport;

            const res = await api.get(`/api/v1.0/players/${playerId}/statistics/`, { params });
            if (thisReq !== reqRef.current) return;

            const rawList = res.data.results || res.data || [];
            const cleanList = rawList
                .filter(t => !(t.name && t.name.toLowerCase().includes('рейтинг')))
                .map(t => ({
                    ...t,
                    matches: deduplicateMatches(t.matches || [])
                }));

            if (fetchPage === 1) {
                setTournaments(cleanList);
            } else {
                setTournaments(prev => mergeTournaments(prev, cleanList));
            }
            enrichClubLogos(cleanList);
            setHasMore(!!res.data.next);
        } catch (e) {
            if (thisReq === reqRef.current) {
                console.error(e);
            }
        } finally {
            if (thisReq === reqRef.current) {
                setLoading(false);
            }
        }
        // api (useAxios()) is a fresh instance every render by design (see
        // useAxios.js) - including it here made loadData itself a new
        // reference every render too, and the mount effect below watches
        // loadData, so it fired on every render: reset to page 1, refetch,
        // re-render, repeat forever. Never actually finishing is exactly
        // what read as "statistics won't load".
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playerId, year, grade, club, sport]);

    useEffect(() => {
        if (inView && hasMore && !loading) {
            setPage(prev => prev + 1);
        }
    }, [inView, hasMore, loading]);

    useEffect(() => {
        setTournaments([]);
        setPage(1);
        setHasMore(true);
        loadData(1);
    }, [loadData]);

    useEffect(() => {
        if (page > 1) {
            loadData(page);
        }
    }, [page, loadData]);

    if (tournaments.length === 0 && loading) {
        return <StatisticLazyLoad count={10} grade={grade} />;
    }

    if (tournaments.length === 0 && !loading) {
        return <div className="mt-3 text-muted text-center">{t("No statistics available")}</div>;
    }

    return (
        <div className="sidebar-wrapper">
            {tournaments.map((tournament, i) => {
                const dedupedMatches = deduplicateMatches(tournament.matches || []);
                // This endpoint's match objects carry no group size (`total`
                // is only ever populated for draw matches, never group ones -
                // ScheduleTable.js can lean on it because its own endpoint
                // fills it in) - reconstructed here off this same player's
                // own matches instead: round-robin means they play every
                // other group member exactly once, so this player's count of
                // matches sharing that `group` id is the group size minus
                // themself.
                const groupSizeById = new Map();
                dedupedMatches.forEach(m => {
                    if (m.group_number == null || !m.group) return;
                    groupSizeById.set(m.group, (groupSizeById.get(m.group) || 0) + 1);
                });
                const groupRoundOf = (m) => {
                    const n = (groupSizeById.get(m.group) || 0) + 1;
                    return getGroupRoundNumber(n, m.match_order);
                };
                // A plain single-elimination ("олимпийка") tournament only
                // ever has draw_number 1 - no place label needed there, the
                // round name alone (F/1/2/...) already says enough. Once a
                // second draw shows up (3rd place playoff, 5-8 bracket,
                // ...), draw 1 needs its own "1 М" label too so it doesn't
                // read as the odd one out missing a place.
                const hasMultipleDraws = dedupedMatches.some(m => m.draw_number > 1);
                // Final first, then semi/quarter/... down to round 1, group-
                // stage matches last of all (group_number set - round_number
                // is always 1 there too, not a real "round" in the knockout
                // sense, so they don't interleave with the draw rounds
                // above them). Within the group block itself, latest tour on
                // top too - same round-robin "tour" ScheduleTable.js derives
                // via getGroupRoundNumber, sorted highest-first same as the
                // knockout rounds above it.
                const uniqueMatches = dedupedMatches
                    .sort((a, b) => {
                        const aGroup = a.group_number != null;
                        const bGroup = b.group_number != null;
                        if (aGroup !== bGroup) return aGroup ? 1 : -1;
                        if (aGroup) return (groupRoundOf(b) ?? 0) - (groupRoundOf(a) ?? 0);
                        return 0; // draws: keep backend order (draw N → 1, final → round 1)
                    });
                const clubName = typeof tournament.club === 'object' ? tournament.club?.name : tournament.club;
                const thumbSrc = tournament.thumbnail || tournament.club_logo || tournament.image || tournament.location_image;
                const statusClass = getStatusClass(tournament);
                return (
                    <React.Fragment key={tournament.id || i}>
                        {/* Same markup/classes as TournamentsList.js's own .tournament-card
                            (and ClubProfileModal.js's identical copy) - deliberately "1 в 1",
                            not just visually similar, so this header always matches the
                            tournament card look everywhere else. This endpoint's tournament
                            objects carry a smaller field set though (no date_range/players/
                            thumbnail) - see formatDateRange/getTournamentStatus above for the
                            parts recomputed locally to fill that gap, and enrichClubLogos
                            above for club_logo (fetched lazily per tournament, not part of
                            this endpoint's own response). No
                            expand/collapse "details" here (unlike the list card this mirrors) -
                            the match table right below already is this tournament's own
                            expanded content, so a second disclosure over the same header
                            would just be redundant. */}
                        <div className="tournament-card player-statistic-card" style={{ cursor: "default" }}>
                            {/* Only this header is the tournament link now - it used to
                                be the whole card (match table included), so missing a
                                name's own small click target, or tapping the current
                                player's own name (never clickable, see nameSpan's
                                currentPlayerId check above and the comment there), fell
                                through to this handler and opened the tournament instead
                                of doing nothing. */}
                            <div
                                className="tournament-card-header-link"
                                style={{ cursor: "pointer" }}
                                onClick={() => history.push(`/tournaments/${tournament.id}`)}
                            >
                                <div className={`tournament-card-date ${statusClass}`}>{formatDateRange(tournament.start_date, tournament.end_date)}</div>
                                <div className="tournament-card-top">
                                    <div className="tournament-card-thumb">
                                        {thumbSrc
                                            ? <img src={thumbSrc.startsWith('http') || thumbSrc.startsWith('/') ? thumbSrc : `/media/${thumbSrc}`} alt="" />
                                            : (clubName || tournament.name)
                                                ? <span style={{ fontSize: "16px", fontWeight: 600 }}>{getInitials(clubName || tournament.name)}</span>
                                                : <FeatherIcon icon="camera-off" />}
                                    </div>
                                    <div className="tournament-card-titles">
                                        <div className="tournament-card-title">{formatTournamentTitle(tournament.name)}</div>
                                        <div className="tournament-card-club">{clubName}</div>
                                        <div className="tournament-card-info">
                                            {tournament.city}
                                            {tournament.city && tournament.location_name && " · "}
                                            {tournament.location_name && (
                                                <span className="tournament-card-location">{tournament.location_name}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <table className="groups-table statistic player-statistic-wrapper">
                                <colgroup>
                                    <col style={{width: "40px"}} />
                                    <col />
                                    <col  style={{width: "50px"}} />
                                    <col />
                                </colgroup>
                                <tbody>
                                    {uniqueMatches.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: "center" }}>
                                                {t("Tournament Participant")}
                                            </td>
                                        </tr>
                                    ) : uniqueMatches.map((match, matchIdx) => {
                                        const currentGrade = grade || tournament.grade || 1;
                                        const isSide2 = match.player_2 === playerId || match.player_4 === playerId || match.player_2?.id === playerId || match.player_4?.id === playerId;

                                        let winnerSide = Number(match.winner_side);
                                        if (!winnerSide && match.sets && match.sets.length > 0) {
                                            let s1 = 0, s2 = 0;
                                            match.sets.forEach(s => {
                                                if (s.game_score_1 > s.game_score_2) s1++;
                                                else if (s.game_score_2 > s.game_score_1) s2++;
                                            });
                                            if (s1 > s2) winnerSide = 1;
                                            else if (s2 > s1) winnerSide = 2;
                                        }
                                        const hasWinner = winnerSide === 1 || winnerSide === 2;

                                        const pTop1 = isSide2 ? match.player_2_name : match.player_1_name;
                                        const pTop2 = isSide2 ? match.player_4_name : match.player_3_name;
                                        const pBot1 = isSide2 ? match.player_1_name : match.player_2_name;
                                        const pBot2 = isSide2 ? match.player_3_name : match.player_4_name;

                                        const idOf = (p) => (p && typeof p === "object" ? p.id : p) || null;
                                        const idTop1 = isSide2 ? idOf(match.player_2) : idOf(match.player_1);
                                        const idTop2 = isSide2 ? idOf(match.player_4) : idOf(match.player_3);
                                        const idBot1 = isSide2 ? idOf(match.player_1) : idOf(match.player_2);
                                        const idBot2 = isSide2 ? idOf(match.player_3) : idOf(match.player_4);

                                        const isTopWinner = isSide2 ? winnerSide === 2 : winnerSide === 1;
                                        const isBotWinner = isSide2 ? winnerSide === 1 : winnerSide === 2;

                                        const groupRound = match.group_number ? groupRoundOf(match) : null;
                                        const roundText = match.group_number
                                            ? <><span className="black-name">{t("Gr")}</span>{groupRound != null && <><br/>{t("R")}{groupRound}</>}</>
                                            : match.round_number ? <>{(match.draw_number > 1 || (match.draw_number === 1 && hasMultipleDraws)) ? <><span className="black-name">{`${match.draw_number}${getOrdinalSuffix(match.draw_number)} ${t("PL")}`}</span><br/></> : ""}{getSmallRoundTitle(match.round_number, match.total, t, i18n, match.match_order, false)}</> : "";

                                        return (
                                            <tr key={match.id || matchIdx}>
                                                <td className="text-muted">
                                                    <label style={{ textAlign: 'right', margin: 0, display: 'block' }}>
                                                        <center>
                                                            {roundText}
                                                        </center>
                                                    </label>
                                                </td>
                                                <td className="text-right">
                                                    {renderTeamCompact(pTop1, pTop2, currentGrade, hasWinner, isTopWinner, idTop1, idTop2, onPlayerClick, playerId)}
                                                </td>
                                                <td style={{ textAlign: "center" }}>
                                                    {renderScoreCompact(match, isSide2)}
                                                </td>
                                                <td className="text-left">
                                                    {renderTeamCompact(pBot1, pBot2, currentGrade, hasWinner, isBotWinner, idBot1, idBot2, onPlayerClick, playerId)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </React.Fragment>
                );
            })}

            {hasMore && (
                <div ref={ref}>
                    {loading && <StatisticLazyLoad count={2} grade={grade} />}
                </div>
            )}
        </div>
    );
};

export default PlayerStatistic;
